"use client";

import { MicOff, Mic, Pause, Play } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useProfileContext } from "../../../context/ProfileContext";

export type PracticeRecordingHandle = {
  getEnglishAudioBlob: () => Promise<Blob | null>;
  getTeluguAudioBlob: () => Promise<Blob | null>;
  getEnglishAudioURL: () => string | null;
  getTeluguAudioURL: () => string | null;
  hasEnglishRecording: () => boolean;
  hasTeluguRecording: () => boolean;
};

const PracticeRecording = React.forwardRef<PracticeRecordingHandle>(
  function PracticeRecordingComponent(_, ref) {
    const { currentWord } = useProfileContext();

    // English recording state
    const [isRecordingEnglish, setIsRecordingEnglish] = useState(false);
    const [englishAudioURL, setEnglishAudioURL] = useState<string | null>(null);
    const [isPlayingEnglish, setIsPlayingEnglish] = useState(false);
    const englishMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const englishAudioChunksRef = useRef<Blob[]>([]);
    const englishAudioRef = useRef<HTMLAudioElement | null>(null);

    // Telugu recording state
    const [isRecordingTelugu, setIsRecordingTelugu] = useState(false);
    const [teluguAudioURL, setTeluguAudioURL] = useState<string | null>(null);
    const [isPlayingTelugu, setIsPlayingTelugu] = useState(false);
    const teluguMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const teluguAudioChunksRef = useRef<Blob[]>([]);
    const teluguAudioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
      if (englishAudioRef.current) {
        englishAudioRef.current.onended = () => setIsPlayingEnglish(false);
      }
    }, [englishAudioURL]);

    useEffect(() => {
      if (teluguAudioRef.current) {
        teluguAudioRef.current.onended = () => setIsPlayingTelugu(false);
      }
    }, [teluguAudioURL]);

    // Expose methods to parent component
    React.useImperativeHandle(ref, () => ({
      getEnglishAudioBlob: async () => {
        if (englishAudioURL) {
          const response = await fetch(englishAudioURL);
          return await response.blob();
        }
        return null;
      },
      getTeluguAudioBlob: async () => {
        if (teluguAudioURL) {
          const response = await fetch(teluguAudioURL);
          return await response.blob();
        }
        return null;
      },
      getEnglishAudioURL: () => englishAudioURL,
      getTeluguAudioURL: () => teluguAudioURL,
      hasEnglishRecording: () => !!englishAudioURL,
      hasTeluguRecording: () => !!teluguAudioURL,
    }));

    // English recording functionality
    const startEnglishRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        englishMediaRecorderRef.current = new MediaRecorder(stream);
        englishAudioChunksRef.current = [];

        englishMediaRecorderRef.current.ondataavailable = (event) => {
          englishAudioChunksRef.current.push(event.data);
        };

        englishMediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(englishAudioChunksRef.current, {
            type: "audio/wav",
          });
          const url = URL.createObjectURL(audioBlob);
          setEnglishAudioURL(url);
          stream.getTracks().forEach((track) => track.stop());
        };

        englishMediaRecorderRef.current.start();
        setIsRecordingEnglish(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Please allow microphone access to record your voice!");
      }
    };

    const stopEnglishRecording = () => {
      if (englishMediaRecorderRef.current && isRecordingEnglish) {
        englishMediaRecorderRef.current.stop();
        setIsRecordingEnglish(false);
      }
    };

    const playEnglishRecording = () => {
      if (englishAudioURL && englishAudioRef.current) {
        if (isPlayingEnglish) {
          englishAudioRef.current.pause();
          setIsPlayingEnglish(false);
        } else {
          englishAudioRef.current.play();
          setIsPlayingEnglish(true);
        }
      }
    };

    // Telugu recording functionality
    const startTeluguRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        teluguMediaRecorderRef.current = new MediaRecorder(stream);
        teluguAudioChunksRef.current = [];

        teluguMediaRecorderRef.current.ondataavailable = (event) => {
          teluguAudioChunksRef.current.push(event.data);
        };

        teluguMediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(teluguAudioChunksRef.current, {
            type: "audio/wav",
          });
          const url = URL.createObjectURL(audioBlob);
          setTeluguAudioURL(url);
          stream.getTracks().forEach((track) => track.stop());
        };

        teluguMediaRecorderRef.current.start();
        setIsRecordingTelugu(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Please allow microphone access to record your voice!");
      }
    };

    const stopTeluguRecording = () => {
      if (teluguMediaRecorderRef.current && isRecordingTelugu) {
        teluguMediaRecorderRef.current.stop();
        setIsRecordingTelugu(false);
      }
    };

    const playTeluguRecording = () => {
      if (teluguAudioURL && teluguAudioRef.current) {
        if (isPlayingTelugu) {
          teluguAudioRef.current.pause();
          setIsPlayingTelugu(false);
        } else {
          teluguAudioRef.current.play();
          setIsPlayingTelugu(true);
        }
      }
    };

    return (
      <div className="space-y-4">
        {/* English Recording Section */}
        <div className="bg-orange-50 p-4 rounded-2xl border-2 border-orange-200">
          <h3 className="text-lg font-bold text-orange-800 mb-3 text-center">
            Record &quot;{currentWord?.english}&quot; in English
          </h3>
          <div className="flex gap-3">
            <button
              onClick={
                isRecordingEnglish
                  ? stopEnglishRecording
                  : startEnglishRecording
              }
              className={`flex-1 font-bold py-3 px-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
                isRecordingEnglish
                  ? "bg-gradient-to-r from-red-400 to-pink-400 text-white animate-pulse"
                  : englishAudioURL
                  ? "bg-gradient-to-r from-green-400 to-emerald-400 text-white hover:shadow-xl"
                  : "bg-gradient-to-r from-orange-400 to-yellow-400 text-white hover:shadow-xl"
              }`}
            >
              {isRecordingEnglish ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
              <span className="text-sm">
                {isRecordingEnglish
                  ? "Stop"
                  : englishAudioURL
                  ? "Re-record"
                  : "Record"}
              </span>
            </button>

            {englishAudioURL && (
              <button
                onClick={playEnglishRecording}
                className="flex-1 bg-gradient-to-r from-blue-400 to-cyan-400 text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
              >
                {isPlayingEnglish ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span className="text-sm">Play</span>
              </button>
            )}
          </div>
          {englishAudioURL && (
            <div className="mt-2 text-center">
              <span className="text-green-600 font-semibold text-sm">
                ✅ English recorded!
              </span>
            </div>
          )}
        </div>

        {/* Telugu Recording Section */}
        <div className="bg-blue-50 p-4 rounded-2xl border-2 border-blue-200">
          <h3 className="text-lg font-bold text-blue-800 mb-3 text-center">
            Record &quot;{currentWord?.telugu}&quot; in Telugu (Comming Soon !!)
          </h3>
          <div className="flex gap-3">
            <button
              disabled
              onClick={
                isRecordingTelugu ? stopTeluguRecording : startTeluguRecording
              }
              style={{
                cursor: "not-allowed",
              }}
              className={`flex-1 font-bold py-3 px-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
                isRecordingTelugu
                  ? "bg-gradient-to-r from-red-400 to-pink-400 text-white animate-pulse"
                  : teluguAudioURL
                  ? "bg-gradient-to-r from-green-400 to-emerald-400 text-white hover:shadow-xl"
                  : "bg-gradient-to-r from-blue-400 to-purple-400 text-white hover:shadow-xl"
              }`}
            >
              {isRecordingTelugu ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
              <span className="text-sm">
                {isRecordingTelugu
                  ? "Stop"
                  : teluguAudioURL
                  ? "Re-record"
                  : "Record"}
              </span>
            </button>

            {teluguAudioURL && (
              <button
                onClick={playTeluguRecording}
                className="flex-1 bg-gradient-to-r from-indigo-400 to-purple-400 text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
              >
                {isPlayingTelugu ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span className="text-sm">Play</span>
              </button>
            )}
          </div>
          {teluguAudioURL && (
            <div className="mt-2 text-center">
              <span className="text-green-600 font-semibold text-sm">
                ✅ Telugu recorded!
              </span>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        {/* <div className="bg-gray-50 p-3 rounded-xl text-center">
          <div className="flex justify-center gap-4 text-sm">
            <span
              className={`flex items-center gap-1 ${
                englishAudioURL
                  ? "text-green-600 font-semibold"
                  : "text-gray-400"
              }`}
            >
              {englishAudioURL ? "✅" : "⭕"} English
            </span>
            <span
              className={`flex items-center gap-1 ${
                teluguAudioURL
                  ? "text-green-600 font-semibold"
                  : "text-gray-400"
              }`}
            >
              {teluguAudioURL ? "✅" : "⭕"} Telugu
            </span>
          </div>
          {englishAudioURL && teluguAudioURL && (
            <div className="mt-2 text-green-700 font-bold text-sm">
              🎉 Both recordings complete! Ready to draw!
            </div>
          )}
        </div> */}

        {/* Hidden audio elements */}
        {englishAudioURL && (
          <audio
            ref={englishAudioRef}
            src={englishAudioURL}
            style={{ display: "none" }}
          />
        )}
        {teluguAudioURL && (
          <audio
            ref={teluguAudioRef}
            src={teluguAudioURL}
            style={{ display: "none" }}
          />
        )}
      </div>
    );
  }
);

export default PracticeRecording;
